import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, UserPlus, Search, Phone, MapPin, Send, 
  MoreVertical, Edit2, Pause, Play, Eye, RefreshCw,
  CheckCircle, Clock, XCircle, Filter, QrCode,
  FileText, DollarSign, AlertCircle, Link2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'invited', label: 'Invited', color: 'bg-amber-100 text-amber-700' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'suspended', label: 'Suspended', color: 'bg-red-100 text-red-700' }
];

const ROLE_OPTIONS = [
  { value: 'MD', label: 'Doctor (MD)' },
  { value: 'Community Consultant', label: 'Community Consultant' }
];

export default function ProviderManagement() {
  const [activeTab, setActiveTab] = useState('providers');
  const [providers, setProviders] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [referralSearch, setReferralSearch] = useState('');
  const [referralStatus, setReferralStatus] = useState('all');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    role: 'MD',
    village: '',
    clinic_name: '',
    commission_rate: 5,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter, roleFilter, referralStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [providersRes, referralsRes, statsRes] = await Promise.all([
        api.get('/provider-management/providers', {
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
            role: roleFilter !== 'all' ? roleFilter : undefined,
            search: searchQuery || undefined
          }
        }),
        api.get('/provider-management/referrals', {
          params: {
            status: referralStatus !== 'all' ? referralStatus : undefined,
            search: referralSearch || undefined
          }
        }),
        api.get('/provider-management/stats/overview')
      ]);
      
      setProviders(providersRes.data);
      setReferrals(referralsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone: '',
      role: 'MD',
      village: '',
      clinic_name: '',
      commission_rate: 5,
      notes: ''
    });
  };

  const handleCreateProvider = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.post('/provider-management/providers', formData);
      toast.success('Provider created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create provider');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProvider = async (e) => {
    e.preventDefault();
    if (!selectedProvider) return;
    setSaving(true);
    
    try {
      await api.put(`/provider-management/providers/${selectedProvider.id}`, formData);
      toast.success('Provider updated successfully');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update provider');
    } finally {
      setSaving(false);
    }
  };

  const handleSendActivation = async (provider) => {
    try {
      const response = await api.post(`/provider-management/providers/${provider.id}/send-activation`);
      toast.success(response.data.message);
      
      // Show activation link in dev mode
      if (response.data.activation_link) {
        toast.info(`DEV: ${response.data.activation_link}`, { duration: 15000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send activation');
    }
  };

  const handleSuspendProvider = async (provider) => {
    if (!confirm(`Suspend ${provider.full_name}?`)) return;
    
    try {
      await api.post(`/provider-management/providers/${provider.id}/suspend`);
      toast.success('Provider suspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend provider');
    }
  };

  const handleReactivateProvider = async (provider) => {
    try {
      await api.post(`/provider-management/providers/${provider.id}/reactivate`);
      toast.success('Provider reactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate provider');
    }
  };

  const handleLookupReferral = async () => {
    if (!referralSearch) return;
    
    try {
      const response = await api.get(`/provider-management/referrals/lookup/${referralSearch}`);
      setSelectedReferral(response.data);
      setShowRedeemModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Referral not found');
    }
  };

  const handleRedeemReferral = async () => {
    if (!selectedReferral) return;
    setSaving(true);
    
    try {
      await api.post(`/provider-management/referrals/${selectedReferral.ref_id}/redeem`, {
        ref_id: selectedReferral.ref_id,
        create_patient: !selectedReferral.is_duplicate
      });
      toast.success('Referral redeemed successfully');
      setShowRedeemModal(false);
      setSelectedReferral(null);
      setReferralSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem referral');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      full_name: provider.full_name,
      phone: provider.phone,
      role: provider.role,
      village: provider.village || '',
      clinic_name: provider.clinic_name || '',
      commission_rate: provider.commission_rate,
      notes: provider.notes || ''
    });
    setShowEditModal(true);
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt?.color || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Provider Management</h1>
          <p className="text-slate-600">Manage external doctors and community consultants</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-provider-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Register Provider
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Total Providers</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.providers.total}</p>
                </div>
                <Users className="h-8 w-8 text-emerald-500" />
              </div>
              <div className="mt-2 flex gap-2 text-xs">
                <Badge className="bg-emerald-100 text-emerald-700">{stats.providers.active} Active</Badge>
                <Badge className="bg-amber-100 text-amber-700">{stats.providers.invited} Invited</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Total Referrals</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.referrals.total}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500" />
              </div>
              <div className="mt-2 flex gap-2 text-xs">
                <Badge className="bg-amber-100 text-amber-700">{stats.referrals.pending} Pending</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Redeemed</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.referrals.redeemed}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Commission Pending</p>
                  <p className="text-2xl font-bold text-purple-600">₹{stats.commission.pending.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-200 p-1">
          <TabsTrigger value="providers" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Providers</TabsTrigger>
          <TabsTrigger value="referrals" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Referrals</TabsTrigger>
          <TabsTrigger value="redeem" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Redeem Code</TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="providers" className="mt-4">
          {/* Filters */}
          <Card className="bg-white shadow-sm mb-4">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, phone, or code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {ROLE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Providers List */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
              ) : providers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No providers found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Provider</th>
                        <th className="text-left p-4 font-medium text-slate-600">Contact</th>
                        <th className="text-left p-4 font-medium text-slate-600">Role</th>
                        <th className="text-center p-4 font-medium text-slate-600">Referrals</th>
                        <th className="text-center p-4 font-medium text-slate-600">Status</th>
                        <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {providers.map((provider) => (
                        <tr key={provider.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                                {provider.provider_code}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{provider.full_name}</p>
                                {provider.clinic_name && (
                                  <p className="text-sm text-slate-500">{provider.clinic_name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="text-slate-900">{provider.phone}</p>
                            {provider.village && (
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {provider.village}
                              </p>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              <Badge variant="outline" className="text-slate-700 w-fit">
                                {provider.role}
                              </Badge>
                              {provider.role === 'MD' && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs w-fit flex items-center gap-1">
                                  <Link2 className="h-3 w-3" />
                                  Referring Doctors
                                </Badge>
                              )}
                              {provider.role === 'Community Consultant' && (
                                <Badge className="bg-teal-100 text-teal-700 text-xs w-fit flex items-center gap-1">
                                  <Link2 className="h-3 w-3" />
                                  Community Workers
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className="font-medium text-slate-900">{provider.total_referrals}</span>
                          </td>
                          <td className="p-4 text-center">
                            <Badge className={getStatusBadge(provider.status)}>
                              {provider.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditModal(provider)}>
                                  <Edit2 className="h-4 w-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                {provider.status === 'invited' && (
                                  <DropdownMenuItem onClick={() => handleSendActivation(provider)}>
                                    <Send className="h-4 w-4 mr-2" /> Send Activation
                                  </DropdownMenuItem>
                                )}
                                {provider.status === 'active' && (
                                  <DropdownMenuItem onClick={() => handleSuspendProvider(provider)} className="text-red-600">
                                    <Pause className="h-4 w-4 mr-2" /> Suspend
                                  </DropdownMenuItem>
                                )}
                                {provider.status === 'suspended' && (
                                  <DropdownMenuItem onClick={() => handleReactivateProvider(provider)} className="text-emerald-600">
                                    <Play className="h-4 w-4 mr-2" /> Reactivate
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals" className="mt-4">
          <Card className="bg-white shadow-sm mb-4">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by REF-ID, patient name, or mobile..."
                    value={referralSearch}
                    onChange={(e) => setReferralSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                    className="pl-10"
                  />
                </div>
                <Select value={referralStatus} onValueChange={setReferralStatus}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="redeemed">Redeemed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardContent className="p-0">
              {referrals.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No referrals found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">REF-ID</th>
                        <th className="text-left p-4 font-medium text-slate-600">Patient</th>
                        <th className="text-left p-4 font-medium text-slate-600">Provider</th>
                        <th className="text-center p-4 font-medium text-slate-600">Priority</th>
                        <th className="text-center p-4 font-medium text-slate-600">Status</th>
                        <th className="text-right p-4 font-medium text-slate-600">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {referrals.map((referral) => (
                        <tr key={referral.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <span className="font-mono font-bold text-emerald-600">{referral.ref_id}</span>
                          </td>
                          <td className="p-4">
                            <p className="font-medium text-slate-900">{referral.patient_name}</p>
                            <p className="text-sm text-slate-500">{referral.patient_mobile}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-slate-900">{referral.provider_name}</p>
                            <p className="text-sm text-slate-500">{referral.provider_code}</p>
                          </td>
                          <td className="p-4 text-center">
                            <Badge className={
                              referral.priority === 'emergency' ? 'bg-red-100 text-red-700' :
                              referral.priority === 'urgent' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {referral.priority}
                            </Badge>
                          </td>
                          <td className="p-4 text-center">
                            <Badge className={
                              referral.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              referral.status === 'redeemed' ? 'bg-blue-100 text-blue-700' :
                              referral.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {referral.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-right text-sm text-slate-500">
                            {new Date(referral.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Redeem Code Tab */}
        <TabsContent value="redeem" className="mt-4">
          <Card className="bg-white shadow-sm max-w-xl mx-auto">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <QrCode className="h-5 w-5 text-emerald-600" />
                Redeem Referral Code
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Enter the REF-ID provided by the patient to look up their referral details.
              </p>
              <div className="flex gap-3">
                <Input
                  placeholder="e.g., DRJ-0001"
                  value={referralSearch}
                  onChange={(e) => setReferralSearch(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookupReferral()}
                  className="text-lg font-mono"
                />
                <Button onClick={handleLookupReferral} className="bg-emerald-600 hover:bg-emerald-700">
                  <Search className="h-4 w-4 mr-2" />
                  Look Up
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Provider Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Register New Provider</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProvider} className="space-y-4">
            <div>
              <Label className="text-slate-700">Full Name *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="Dr. John Smith"
                required
              />
            </div>
            <div>
              <Label className="text-slate-700">Phone Number *</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+91 9876543210"
                required
              />
            </div>
            <div>
              <Label className="text-slate-700">Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role === 'Community Consultant' && (
              <div>
                <Label className="text-slate-700">Village *</Label>
                <Input
                  value={formData.village}
                  onChange={(e) => setFormData({...formData, village: e.target.value})}
                  placeholder="Village name"
                  required
                />
              </div>
            )}
            {formData.role === 'MD' && (
              <div>
                <Label className="text-slate-700">Clinic Name</Label>
                <Input
                  value={formData.clinic_name}
                  onChange={(e) => setFormData({...formData, clinic_name: e.target.value})}
                  placeholder="Clinic or hospital name"
                />
              </div>
            )}
            <div>
              <Label className="text-slate-700">Commission Rate (%)</Label>
              <Input
                type="number"
                value={formData.commission_rate}
                onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value) || 0})}
                min="0"
                max="100"
                step="0.5"
              />
            </div>
            <div>
              <Label className="text-slate-700">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                {saving ? 'Creating...' : 'Create Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Provider Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Provider</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProvider} className="space-y-4">
            <div>
              <Label className="text-slate-700">Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-slate-700">Phone Number</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-slate-700">Village</Label>
              <Input
                value={formData.village}
                onChange={(e) => setFormData({...formData, village: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-slate-700">Clinic Name</Label>
              <Input
                value={formData.clinic_name}
                onChange={(e) => setFormData({...formData, clinic_name: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-slate-700">Commission Rate (%)</Label>
              <Input
                type="number"
                value={formData.commission_rate}
                onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value) || 0})}
                min="0"
                max="100"
                step="0.5"
              />
            </div>
            <div>
              <Label className="text-slate-700">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Redeem Modal */}
      <Dialog open={showRedeemModal} onOpenChange={setShowRedeemModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Redeem Referral</DialogTitle>
          </DialogHeader>
          {selectedReferral && (
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <p className="text-sm text-emerald-600">Reference ID</p>
                <p className="text-2xl font-mono font-bold text-emerald-700">{selectedReferral.ref_id}</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Patient:</span>
                  <span className="font-medium text-slate-900">{selectedReferral.patient_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Mobile:</span>
                  <span className="text-slate-900">{selectedReferral.patient_mobile}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Provider:</span>
                  <span className="text-slate-900">{selectedReferral.provider_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Priority:</span>
                  <Badge className={
                    selectedReferral.priority === 'emergency' ? 'bg-red-100 text-red-700' :
                    selectedReferral.priority === 'urgent' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {selectedReferral.priority}
                  </Badge>
                </div>
                {selectedReferral.chief_complaint && (
                  <div>
                    <span className="text-slate-600">Chief Complaint:</span>
                    <p className="text-slate-900 mt-1">{selectedReferral.chief_complaint}</p>
                  </div>
                )}
                {selectedReferral.reason_translated && (
                  <div>
                    <span className="text-slate-600">Reason (Translated):</span>
                    <p className="text-slate-900 mt-1">{selectedReferral.reason_translated}</p>
                    {selectedReferral.translation_tag && (
                      <Badge className="bg-blue-100 text-blue-700 mt-1">
                        {selectedReferral.translation_tag}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {selectedReferral.is_duplicate && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Duplicate Patient</p>
                    <p className="text-sm text-amber-700">This patient already exists in the system. Redeeming will link to the existing record.</p>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRedeemModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleRedeemReferral} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={saving || selectedReferral.status !== 'pending'}
                >
                  {saving ? 'Processing...' : 'Redeem & Register Patient'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
